import type {
  ChangedInterval,
  ChangedResource
} from '@lmc/ts-common/lib/utils/rota-socket-response'
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import isoWeek from 'dayjs/plugin/isoWeek'
import weekday from 'dayjs/plugin/weekday'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { useEffect, useRef } from 'react'
import { useQueryClient } from 'react-query'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { io, type Socket as SocketIo } from 'socket.io-client'
import CONSTANTS from 'utils/constants'
import { resourceToQueryKeyMap } from './constants'
import {
  getQueryInvalidationPredicate,
  normaliseInvalidateMessage
} from './utils'

dayjs.extend(weekday)
dayjs.extend(isoWeek)
dayjs.extend(isBetween)

/** Flattened shape the invalidation predicate works with. */
export type InvalidateMessage = {
  changedResource: ChangedResource
  changedInterval?: ChangedInterval
  day?: string
  startDate?: string
  endDate?: string
}

/**
 * Shape of the `Invalidate` event as it arrives over the socket. The rota
 * backend nests the interval and dates under `params`; the flat fields are kept
 * for backwards compatibility with older payloads.
 */
export type SocketInvalidateMessage = InvalidateMessage & {
  params?: {
    type?: ChangedInterval
    day?: string
    startDate?: string
    endDate?: string
  }
}

const useQueryInvalidation = () => {
  const queryClient = useQueryClient()
  const userID: string = useSelector(
    (state: { global: { user: { id: string } } }) => state.global.user.id
  )
  const socketRef = useRef<SocketIo | null>(null)
  const location = useLocation()

  const flags = useFeatureFlags({
    flagNames: ['rostering_web_socket']
  })?.data

  const flagsWebSocket = flags?.rostering_web_socket?.enabled

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    const url = CONSTANTS.VITE_APP_ROTA_API_WS
    const path = CONSTANTS.VITE_APP_ROTA_API_WS_PATH

    if (!socketRef.current && userID) {
      socketRef.current = io(url, {
        path,
        query: {
          user_id: userID
        },
        autoConnect: false,
        transports: ['websocket']
      })
    }

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect()
        socketRef.current = null
      }
    }
  }, [userID])

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (!flagsWebSocket) return

    const isRota = location.pathname.startsWith('/rota')

    if (socketRef.current && isRota && !socketRef.current.connected) {
      socketRef.current = socketRef.current.connect()
    }
  }, [location, flagsWebSocket])

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (flagsWebSocket && socketRef.current) {
      const onInvalidateQuery = (message: SocketInvalidateMessage) => {
        const normalisedMessage = normaliseInvalidateMessage(message)
        const queryKeysToInvalidate =
          resourceToQueryKeyMap[normalisedMessage.changedResource]

        if (!queryKeysToInvalidate?.length) return

        queryClient.invalidateQueries({
          predicate: getQueryInvalidationPredicate(normalisedMessage)
        })
      }

      const handleConnect = () => {
        socketRef.current?.on('Invalidate', onInvalidateQuery)
      }

      socketRef.current.on('connect', handleConnect)

      return () => {
        socketRef.current?.off('connect', handleConnect)
        socketRef.current?.off('Invalidate', onInvalidateQuery)
      }
    }

    return () => {}
  }, [socketRef.current, flagsWebSocket])
}

export default useQueryInvalidation
